import type { Route } from './+types/services.weekly';
import { Link } from 'react-router';
import { LeafMark } from '~/components/marketing/leaf-mark';
import { Breadcrumbs, breadcrumbSchemaItems } from '~/components/breadcrumbs';
import { RelatedPages } from '~/components/related-pages';
import { buildMeta, schemaFor, breadcrumbSchema } from '~/lib/seo';
import { SITE, PRICING } from '~/data/site';
import { useBranding, brandingFromMatches } from '~/lib/branding';

export const meta: Route.MetaFunction = ({ matches }) => {
  const branding = brandingFromMatches(matches);
  return [
    ...buildMeta({
      title: 'Weekly Cleaning Service | Tampa Bay',
      description:
        `Professional weekly home cleaning in Tampa Bay. Same crew every week. Per-square-foot pricing starts at ${PRICING.weeklyRate.toFixed(2)}/sqft. ${PRICING.minimum} minimum. Schedule your first clean today.`,
      path: '/weekly',
      siteName: branding.name,
      origin: branding.origin,
      jsonLd: [
        schemaFor('/weekly', branding.isNewLeaf),
        breadcrumbSchema(breadcrumbSchemaItems('/weekly')),
      ].filter((s): s is Record<string, unknown> => !!s),
    }),
  ];
};

export default function WeeklyServicePage() {
  const branding = useBranding();
  const examplePrice = 2400 * PRICING.weeklyRate;

  return (
    <main id="main-content">
      <Breadcrumbs path="/weekly" pageLabel="Weekly Cleaning" />
      {/* ── HEADER ───────────────────────────────────────── */}
      <section className="section">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Recurring Service</p>
          <h1 className="display-lg page-h1-full">
            Same crew, <em className="italic-display">every week.</em>
          </h1>
          <p className="lede chapter-lede">
            Weekly cleaning from professionals who know your home. Same technicians,
            same standard, every visit. That consistency is how homes stay right all year.
          </p>
        </div>
      </section>

      {/* ── PRICING ───────────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <h2 className="display-md">How much does it cost?</h2>
          <div className="price-example price-example-mt reveal">
            <h3 className="display-sm text-bone">${PRICING.weeklyRate.toFixed(2)} per square foot</h3>
            <div className="price-ex-line price-ex-block-mt">
              <span>Example: 2,400 sqft home<br /><small className="text-mute">Your actual price depends on your home&rsquo;s size.</small></span>
              <span className="price-ex-total price-ex-total-sm">${Math.round(examplePrice)}</span>
            </div>
            <div className="price-ex-line">
              <span>Minimum on every service<br /><small className="text-mute">Even your smallest visits are a full professional clean.</small></span>
              <span className="price-ex-total price-ex-total-sm">${PRICING.minimum}</span>
            </div>
            <p className="marginalia price-example-marginalia">
              See your exact price for your home in under 60 seconds.
            </p>
          </div>
          <div className="section-cta-left">
            <Link to="/quote" className="btn btn-brass">Calculate your price →</Link>
          </div>
        </div>
      </section>

      {/* ── WHY WEEKLY ────────────────────────────────────── */}
      <section className="section-sm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Why the best homes go weekly</p>
          <h2 className="display-md">Rhythm is everything.</h2>
          <div className="feature-list reveal">
            <div className="feature-item">
              <h3 className="feature-title">Same crew stays longer</h3>
              <p className="feature-text">
                Our team walks into a home they know. They remember where the dog sleeps,
                which baseboards get dusty, how you like your kitchen. That&rsquo;s not
                just efficiency &mdash; it&rsquo;s care.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Your home stays cleaner</h3>
              <p className="feature-text">
                A week between visits means dust hasn&rsquo;t settled, spills haven&rsquo;t
                hardened. Weekly cleaning is preventive. You&rsquo;re not resetting the
                house every visit.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Deep cleans happen naturally</h3>
              <p className="feature-text">
                Rotating attention to detail. Week one: baseboards. Week two: inside
                cabinets. Week three: behind the fridge. By month four, nothing was
                neglected.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Your crew knows your family</h3>
              <p className="feature-text">
                It matters. Trust builds. Questions get answered in person. When your
                aunt visits and says your house is immaculate, your cleaners own that win
                too.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ───────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <h2 className="display-md">What we clean every visit</h2>
          <div className="checklist-grid reveal">
            <div className="checklist-col">
              <h3 className="checklist-heading">Kitchen</h3>
              <ul className="checklist">
                <li>Countertops and backsplash</li>
                <li>Sink and faucet</li>
                <li>Appliances (exterior)</li>
                <li>Cabinet fronts and handles</li>
                <li>Baseboards</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Bathrooms</h3>
              <ul className="checklist">
                <li>Toilets and sinks</li>
                <li>Mirrors and fixtures</li>
                <li>Shower/tub clean</li>
                <li>Floors and grout</li>
                <li>Ventilation fans</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Living Spaces</h3>
              <ul className="checklist">
                <li>Vacuuming and mopping</li>
                <li>Dusting surfaces</li>
                <li>Light switches and doors</li>
                <li>Windows (interior)</li>
                <li>Baseboards and trim</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Bedrooms</h3>
              <ul className="checklist">
                <li>Floors vacuumed</li>
                <li>Dusting all surfaces</li>
                <li>Organizing clutter</li>
                <li>Baseboards</li>
                <li>Light switches</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── FIRST VISIT UPGRADE ──────────────────────────── */}
      <section className="section-sm">
        <div className="container-narrow">
          <h2 className="display-md">Your first week is special.</h2>
          <p className="lede chapter-lede">
            Add a deep-clean upgrade to your first weekly visit: <strong>+${PRICING.deepUpgradeRate.toFixed(2)}/sqft</strong>.
            That&rsquo;s when we dive into baseboards, inside cabinets, behind appliances, and
            spaces that normal weekly cleaning doesn&rsquo;t touch. After that, your crew
            rotates focus each week so nothing ever gets neglected.
          </p>
          <p className="lede chapter-lede chapter-lede-mt">
            Example: 2,400 sqft home, first visit would be ${Math.round(examplePrice + (2400 * PRICING.deepUpgradeRate))}.
          </p>
        </div>
      </section>

      {/* ── WHY NLC ────────────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Why {branding.name}</p>
          <h2 className="display-md">We're built on <em className="italic-display">consistency.</em></h2>
          <div className="value-props reveal">
            <div className="value-prop">
              <h3>W-2 Employees Only</h3>
              <p>Your crew is employed by us. Benefits, training, accountability — all real.</p>
            </div>
            <div className="value-prop">
              <h3>Insured &amp; Background Checked</h3>
              <p>Every technician vetted. Insurance covers your home. You have recourse.</p>
            </div>
            <div className="value-prop">
              <h3>Per-Square-Foot Pricing</h3>
              <p>No surprises. No upsells. The price is the price, every week.</p>
            </div>
            <div className="value-prop">
              <h3>Same Crew Strategy</h3>
              <p>You meet your team. They learn your home. Continuity matters.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────── */}
            <RelatedPages path="/weekly" />

      <section className="cta-final">
        <div className="container-narrow cta-inner">
          <p className="cta-eyebrow">Ready to start?</p>
          <h2>Schedule your first weekly clean</h2>
          <p className="cta-lede">See your price for your home. No obligation. Takes 60 seconds.</p>
          <Link to="/quote" className="btn btn-brass btn-lg">Get your price →</Link>
        </div>
      </section>
    </main>
  );
}
